import React, { FC, useContext, useEffect, useState } from 'react';

import CustomDialog from '../../common/components/CustomDialog';
import { Operation, Resource } from '../../common/constants';
import ConfirmationButtons from '../ConfirmationButtons/ConfirmationButtons';
import { PermissionInput } from '../../types/graphql-global-types';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import useStyles from './styles';
import PermissionsManager from '../PermissionsManager';
import { CustomDialogProps } from '../../common/components/CustomDialog/CustomDialog';
import { useSetUserPermissions } from '../../hooks/mutations/useSetUserPermissions';
import { GetAllUsers_getAllUsers_users } from '../../graphql/user/types/GetAllUsers';

interface IUserPermissionsManager extends CustomDialogProps {
  user:
    | Pick<GetAllUsers_getAllUsers_users, 'id' | 'permissions'>
    | null
    | undefined;
}

const initialPermissions: PermissionInput[] = Object.keys(Resource).map(
  (resource) => ({ resource }),
);

const UserPermissionsManager: FC<IUserPermissionsManager> = (props) => {
  const { user, onClose } = props;
  const classes = useStyles();

  const { canI } = useContext<IUserContext>(UserContext);
  const canEditRoles = canI(Operation.UPDATE, Resource.ROLES);

  const [permissionsInput, setPermissionsInput] = useState<
    (PermissionInput | null)[] | null
  >(initialPermissions);

  const [setUserPermissions, { loading: setUserPermissionsLoading }] =
    useSetUserPermissions();

  const handleChange = (permission: PermissionInput | null) => {
    setPermissionsInput(
      (_permissions) =>
        _permissions?.map((_permission) =>
          _permission?.resource === permission?.resource
            ? { ..._permission, ...permission }
            : _permission,
        ) ?? [],
    );
  };

  useEffect(() => {
    if (user?.permissions?.length) {
      const permissions =
        user?.permissions?.map((permission) => {
          if (!permission) return;
          const { __typename, ...others } = permission;
          return others;
        }) ?? initialPermissions;

      setPermissionsInput(permissions as PermissionInput[]);
    }
  }, [user]);

  const handleSave = async () => {
    const result = await setUserPermissions({
      variables: { input: { userId: user?.id, permissions: permissionsInput } },
    });
    if (result?.data?.setUserPermissions && onClose) onClose();
  };

  return (
    <CustomDialog
      {...props}
      loading={setUserPermissionsLoading}
      className={classes.root}
      actions={
        <ConfirmationButtons
          disableConfirm={!canEditRoles || setUserPermissionsLoading}
          handleCancel={() => onClose && onClose()}
          handleConfirm={handleSave}
        />
      }>
      <PermissionsManager
        onChange={handleChange}
        permissions={permissionsInput}
      />
    </CustomDialog>
  );
};

export default UserPermissionsManager;
